"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useAlertActions = exports.canRequestOfflineService = void 0;
const react_1 = require("react");
const mutations_1 = require("../../graphql/mutations");
const SensorAlert_1 = require("../../types/SensorAlert");
const amplifyBootstrap_1 = require("../../utils/amplifyBootstrap");
const useFilterBaselineReset_1 = require("../hvac/useFilterBaselineReset");
const WATER_ALERT_TYPES = new Set([
    SensorAlert_1.SensorAlertType.Leak,
    SensorAlert_1.SensorAlertType.LowEC,
    SensorAlert_1.SensorAlertType.HighEC,
    SensorAlert_1.SensorAlertType.LowTC,
    SensorAlert_1.SensorAlertType.HighTC,
]);
const HUMIDITY_ALERT_TYPES = new Set([
    SensorAlert_1.SensorAlertType.LowHumidity,
    SensorAlert_1.SensorAlertType.HighHumidity,
]);
const ELECTRICAL_ALERT_TYPES = new Set([
    SensorAlert_1.SensorAlertType.LowMCTCurrent,
    SensorAlert_1.SensorAlertType.HighMCTCurrent,
    SensorAlert_1.SensorAlertType.LowBCTCurrent,
    SensorAlert_1.SensorAlertType.HighBCTCurrent,
    SensorAlert_1.SensorAlertType.LowVoltage,
    SensorAlert_1.SensorAlertType.HighVoltage,
]);
// HVAC service-request routing. HVAC_DEGRADED (efficiency drop), HVAC_CT_FAULT (dead/loose CT
// clamp), HVAC_SENSOR_FAULT, HVAC_COMPRESSOR_FAULT and HIGH_CURRENT (over 50 A or the nameplate
// RLA) have no dedicated service mutation, so they route through the same HVAC service request
// as a filter alert. Without membership here they hit the acknowledge fallback and silently
// dismiss instead of requesting service. These five also send `alertType`, so the cloud files
// them as HVAC_SERVICE instead of a filter job.
// None of them gets the "Order filter" action — that is keyed separately off
// HVACfilter/HvacFilter in useActiveAlerts.
const HVAC_SERVICE_ALERT_TYPES = new Set([
    SensorAlert_1.SensorAlertType.HvacDegraded,
    SensorAlert_1.SensorAlertType.HvacCtFault,
    SensorAlert_1.SensorAlertType.HvacCompressorFault,
    SensorAlert_1.SensorAlertType.HvacSensorFault,
    SensorAlert_1.SensorAlertType.HighCurrent,
]);
const HVAC_FILTER_ALERT_TYPES = new Set([
    SensorAlert_1.SensorAlertType.HVACfilter,
    SensorAlert_1.SensorAlertType.HvacFilter,
    ...HVAC_SERVICE_ALERT_TYPES,
]);
const OFFLINE_ALERT_TYPE_SET = new Set(SensorAlert_1.OFFLINE_ALERT_TYPES);
/**
 * Service-request mutation for an OFFLINE alert, routed by the affected
 * sensor's type — the alert type alone doesn't say which domain resolver
 * owns the visit. The generic createServiceRequest mutation can't be used
 * here: it requires customerId and isn't @aws_api_key, so the hub can't
 * call it; the domain mutations derive the customer from hubId server-side.
 * Returns null when no service flow exists for the type (hub-offline alerts).
 */
function offlineServiceQuery(sensorType) {
    if (!sensorType)
        return null;
    if (sensorType === 'LEAK_DETECTOR' || sensorType === 'WATER_QUALITY') {
        return mutations_1.requestWaterLeakService;
    }
    if (sensorType === 'POWER_MONITOR' || sensorType === 'POWER') {
        return mutations_1.requestPowerMonitorService;
    }
    if (sensorType.startsWith('HVAC')) {
        return mutations_1.requestFilterService;
    }
    if (sensorType === 'AIR_QUALITY') {
        return mutations_1.requestAirQualityService;
    }
    return null;
}
/** Whether an offline alert on this sensor type can offer "Request service". */
function canRequestOfflineService(sensorType) {
    return offlineServiceQuery(sensorType) !== null;
}
exports.canRequestOfflineService = canRequestOfflineService;
function useAlertActions(hubId, authMode = 'userPool') {
    // The web-hub authenticates with API key (`authenticationType: 'apiKey'`),
    // so it must call the `@aws_api_key`-annotated mutation. Mobile uses Cognito
    // and stays on the default user-pool mutation.
    const { resetFilterBaseline } = (0, useFilterBaselineReset_1.useFilterBaselineReset)(authMode);
    // Returns true on success so callers can gate their toast / UI dismiss.
    // Throws are caught and converted to `false`; the caller does NOT need to
    // try/catch, but MUST check the return value before showing a success
    // toast — otherwise it'll lie about a failed mutation.
    const handleAction = (0, react_1.useCallback)(async (actionId, alertId, sensorId, alertType, sensorType) => {
        if (!hubId)
            return false;
        try {
            switch (actionId) {
                case 'clearAlert': {
                    await amplifyBootstrap_1.API.graphql({
                        query: authMode === 'apiKey'
                            ? mutations_1.updateSensorAlertAcknowledgedStateAnonymous
                            : mutations_1.updateSensorAlertAcknowledgedState,
                        variables: { input: { id: alertId, isAcknowledged: true } },
                    });
                    return true;
                }
                case 'requestService': {
                    const input = { sensorId, hubId };
                    if (alertType && OFFLINE_ALERT_TYPE_SET.has(alertType)) {
                        // Never fall through to the acknowledge fallback for offline
                        // alerts — that would silently dismiss instead of requesting
                        // service. Fail loudly (caller shows its error toast).
                        const query = offlineServiceQuery(sensorType);
                        if (!query)
                            return false;
                        await amplifyBootstrap_1.API.graphql({ query, variables: { input } });
                        return true;
                    }
                    if (alertType && HVAC_FILTER_ALERT_TYPES.has(alertType)) {
                        // Filter alerts never send alertType: a stage whose schema predates the field
                        // would reject it, and must not take filter requests down with it.
                        const hvacService = HVAC_SERVICE_ALERT_TYPES.has(alertType);
                        await amplifyBootstrap_1.API.graphql({
                            query: mutations_1.requestFilterService,
                            variables: { input: hvacService ? { ...input, alertType } : input },
                        });
                    }
                    else if (alertType && ELECTRICAL_ALERT_TYPES.has(alertType)) {
                        await amplifyBootstrap_1.API.graphql({ query: mutations_1.requestPowerMonitorService, variables: { input } });
                    }
                    else if (alertType && HUMIDITY_ALERT_TYPES.has(alertType)) {
                        await amplifyBootstrap_1.API.graphql({ query: mutations_1.requestHumidityService, variables: { input } });
                    }
                    else if (alertType && WATER_ALERT_TYPES.has(alertType)) {
                        await amplifyBootstrap_1.API.graphql({ query: mutations_1.requestWaterLeakService, variables: { input } });
                    }
                    else {
                        // Fallback: acknowledge the alert
                        await amplifyBootstrap_1.API.graphql({
                            query: authMode === 'apiKey'
                                ? mutations_1.updateSensorAlertAcknowledgedStateAnonymous
                                : mutations_1.updateSensorAlertAcknowledgedState,
                            variables: { input: { id: alertId, isAcknowledged: true } },
                        });
                    }
                    return true;
                }
                case 'orderFilter': {
                    await amplifyBootstrap_1.API.graphql({
                        query: mutations_1.orderFilter,
                        variables: {
                            input: { sensorId, hubId, filterType: 'Standard', quantity: 1 },
                        },
                    });
                    return true;
                }
                // "I replaced it" — flag a fresh baseline capture AND acknowledge
                // this alert. It replaces `clearAlert` on filter cards rather than
                // adding a fourth button: the hub's alert card is height-boxed by
                // the 480px display, and a plain dismiss on a filter alert is close
                // to useless anyway (the hourly Lambda mints a new row next run
                // while health is still under 60%).
                case 'resetFilterBaseline': {
                    return resetFilterBaseline(sensorId, [alertId]);
                }
            }
            return false;
        }
        catch (error) {
            console.error(`Alert action '${actionId}' failed:`, error);
            return false;
        }
    }, [hubId, authMode, resetFilterBaseline]);
    return { handleAction };
}
exports.useAlertActions = useAlertActions;
